import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TheoryCard } from "@/components/dossier/TheoryCard";
import { CommunityResults } from "@/components/dossier/CommunityResults";
import { track } from "@/lib/analytics";

function VoteRules({ user }) {
  return (
    <div className="mt-4 max-w-[650px] text-[13px] leading-relaxed text-[var(--luma-muted)]">
      <p data-testid="anonymous-vote-notice">{user ? "Stai votando con il tuo account." : "Puoi leggere e votare anche senza account."} La scelta confermata è definitiva.</p>
      <details className="mt-1" data-testid="vote-rules-details">
        <summary className="inline-flex min-h-[44px] cursor-pointer items-center font-semibold text-[var(--luma-action)]">Come funziona il voto</summary>
        <ul className="ml-4 list-disc space-y-1 pb-1">
          <li>Prima di premere «Conferma la mia scelta» non viene salvato nulla. Dopo la conferma il voto non si può cambiare.</li>
          <li>Senza account, un identificativo anonimo salvato in questo browser registra la scelta: un voto per mistero da questo browser. Non garantisce un solo voto fra browser o dispositivi diversi.</li>
          <li>Per limitare gli abusi, da una stessa rete sono accettati al massimo 10 voti anonimi per ogni mistero.</li>
          <li>I voti anonimi contano nelle percentuali ma non nella classifica. Con un account puoi associare al profilo le scelte di questo browser sui misteri ancora aperti.</li>
          <li>Dettagli nell'<Link to="/privacy" className="underline">informativa privacy</Link>.</li>
        </ul>
      </details>
    </div>
  );
}

function ConfirmBox({ selT, busy, err, onConfirm, user }) {
  return (
    <section data-testid="confirm-box" aria-label="Conferma della scelta" className="border-t border-[var(--luma-line)] pt-6">
      <p data-testid="selection-note" aria-live="polite" className="text-[15px]">
        {selT ? <>Selezione provvisoria: <b>{selT.titolo}</b></> : "Nessuna teoria selezionata. Puoi continuare a leggere."}
      </p>
      <button type="button" data-testid="confirm-vote-button" disabled={!selT || busy} aria-busy={busy} onClick={onConfirm} className="luma-btn mt-4">
        {busy ? "Registrazione…" : "Conferma la mia scelta"}
      </button>
      {err && <p role="alert" data-testid="vote-error" className="mt-3 text-sm font-semibold text-red-700">{err}</p>}
      <VoteRules user={user} />
    </section>
  );
}

export function VotePanel({ d, reload }) {
  const { user } = useAuth();
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const voted = !!d.mio_voto;
  const locked = voted || !d.votabile;
  const winners = d.risoluzione?.outcome_ids || [];
  const selT = d.teorie.find((t) => t.id === sel);

  const confirm = async () => {
    if (!sel || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.post(`/dossiers/${d.id}/vote`, { theory_id: sel });
      setSaved(true);
      track("theory_vote", { dossier_id: d.id, theory_id: sel });
      setSel(null);
    } catch (e) {
      const m = errMsg(e);
      setErr(m);
      toast.error(m);
    } finally {
      await reload();
      setBusy(false);
    }
  };

  const intro = voted ? "Hai già fatto la tua scelta: tutte le teorie restano da leggere."
    : d.votabile ? "Leggi le interpretazioni e confronta le idee. Se una ti convince, puoi sceglierla."
      : "Le scelte per questo mistero sono chiuse: le teorie restano tutte da leggere.";

  return (
    <section data-testid="vote-panel" className="space-y-6" aria-labelledby="theories-title">
      <div className="space-y-2 border-t border-[var(--luma-line)] pt-7">
        <h2 id="theories-title" className="text-2xl font-extrabold sm:text-[32px]">Le teorie da esplorare</h2>
        <p className="text-[var(--luma-muted)]">{intro}</p>
      </div>
      <div className="space-y-4 sm:space-y-5">
        {d.teorie.map((t, i) => (
          <TheoryCard key={t.id} t={t} idx={i + 1} picked={sel === t.id} mine={d.mio_voto === t.id} winner={winners.includes(t.id)} choice={d.scelta_lumacofono === t.id}
            locked={locked} onPick={() => { setSel(sel === t.id ? null : t.id); setErr(""); }} expanded={!!open[t.id]} onToggle={() => setOpen({ ...open, [t.id]: !open[t.id] })} />
        ))}
      </div>
      {!locked && <ConfirmBox selT={selT} busy={busy} err={err} onConfirm={confirm} user={user} />}
      {voted && saved && (
        <div data-testid="vote-success" role="status" className="rounded-[18px] border border-emerald-600/30 bg-emerald-50 p-5 text-[15px]">
          <p className="flex items-center gap-2 font-extrabold text-emerald-800"><CheckCircle2 size={18} /> Scelta registrata. Il voto è definitivo.</p>
          <a href="#continua" data-testid="vote-next-link" className="mt-1 inline-flex min-h-[44px] items-center font-bold text-[var(--luma-action)] hover:underline">Scopri un altro mistero ↓</a>
        </div>
      )}
      {d.percentuali_visibili && <CommunityResults d={d} />}
      {d.teorie.some((t) => t.attribuzioni.some((a) => a.tipo === "mondo")) && <p data-testid="world-disclaimer" className="text-xs leading-relaxed text-[var(--luma-muted)]">{d.disclaimer}</p>}
    </section>
  );
}
